import { Product, isInLineMiniApp } from '@play-co/gcinstant';
import { GCInstant as platform } from 'src/lib/gcinstant';
import StateObserver, { user } from 'src/StateObserver';
import i18n from '../i18n/i18n';
import { trackPinchConversion } from 'src/lib/analytics/events';
import { track } from 'src/lib/analytics/utils';
import { State } from 'src/state';
import { devSettings } from 'src/lib/settings';
import { setPurchaseTime } from 'src/state/analytics';
import {
  delay,
  isPlatformGuest,
  parseAmount,
  serverSyncedLocalTime,
  waitForItPromise,
  waitForVisibilityChange,
  withLoading,
} from 'src/lib/utils';
import { cancelableStatePromise } from 'src/lib/statePromise';
import { duration } from 'src/replicant/utils/duration';
import { ProductId } from '../../replicant/iap';
import * as Iap from '../../replicant/iap';
import { openPopupPromise } from '../popups/popupOpenClose';
import * as Inventory from '../../replicant/inventory';
import { realMoneyPriceToReadableString } from './iapConversion';
import { Currencies, CurrencyAnalytics } from 'src/replicant/analytics/events';
import { isPlatformNative } from '../native/native.util';
import features from 'src/replicant/ruleset/features';
import { openWebPurchaseSequence } from 'src/game/iap/iap.sequence';
import {
  shopBundlesEnablePurchaseSimulation,
  shopBundlesIsPurchaseSimulationEnabled,
} from 'src/game/minigame/match2-timestep/shopbundles/match2-shopbundles.utils';
import {
  productIds,
  type ProductReward,
} from '../../replicant/iap/iap.ruleset';
import { captureGenericError } from '../sentry';
import { DeepLink } from 'src/game/launch/deepLinks';
import { isIapReady } from 'src/game/shop/Helpers';
import {
  guessInstalledNativeAppPlatform,
  openNativeAppStorePage,
} from 'src/game/native/native.util';
import {
  PaymentMethod,
  PurchaseCompleteResult,
  PurchaseMethod,
  PurchaseProvider,
  WebPaymentMethod,
} from 'src/game/iap/iap.types';
import { State as ReplicantState } from 'src/replicant/State';
import { Feature } from 'src/replicant/analytics';
import { startNativeCrossplay } from 'src/game/crossplay/crossplay.sequence';
import {
  trackPurchaseFailure,
  trackPurchaseSuccess,
  trackPurchaseTapped,
} from 'src/game/iap/analytics/iap.purchase';
import { trackRevenue } from 'src/game/iap/analytics/iap.revenue';
import {
  IAPAnalytics,
  RevenueAnalytics,
} from 'src/game/iap/analytics/analytics.types';
import {
  getRevenueBasePrice,
  getRevenueGrossAnalytics,
  predictRevenueGrossAnalytics,
} from 'src/game/iap/analytics/iap.gross.revenue';
import {
  hideLoadingAnimation,
  showLoadingAnimation,
} from 'src/game/animation/loading';
import {
  purchaseCanceledSequence,
  purchaseCompleteSequence,
  purchaseFailedSequence,
} from 'src/game/iap/iap.after.purchase.sequence';
import { analytics as analyticsModule } from '@play-co/gcinstant';
import { methodToProviderMapping } from 'src/game/iap/iap.utils';
import { promptLINELoginNew } from 'src/game/guest/guest.sequence';
import {
  buyProductNew,
  isLineIAPAvailable,
} from 'src/game/iap/component/iap.api';

type CompletedPurchase = { product: Product };
export type PurchaseResponse = CompletedPurchase | { redirectUrl: string };

export const isPurchaseRedirect = (
  response: PurchaseResponse,
): response is { redirectUrl: string } => 'redirectUrl' in response;

// Import game catalog to platform, ONLY for local development !!!
export function importProductsCatalogToPlatform() {
  if (!process.env.REPLICANT_OFFLINE || !devSettings.get('enableIAP')) return;

  platform.catalog = [];

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const { ruleset } = Iap;
  for (let index = 0; index < ruleset.productIds.length; index++) {
    const productId = ruleset.productIds[index];
    const price = ruleset.prices[productId];
    const icon = '';

    platform.catalog[index] = {
      title: productId,
      id: productId,
      description: productId,
      imageURI: icon,
      price: formatter.format(price / 100),
      currencyCode: 'USD',
    };
  }
}

// TODO Uncomment after Apple approval
// export const arePaymentsAvailable = ({ iap }: State) =>
//   iap.ready && !isPlatformGuest(platform);

/** @deprecated Use {@link isPaymentAvailable} instead. Just checks `iap.ready` — too simple. */
export const arePaymentsAvailable = ({ iap }: State) => iap.ready;

/**
 * Unified check for whether IAP features should be available to the user.
 *
 * Two-phase strategy:
 * - Once `iap.resolved` is true, uses `iap.ready` as the definitive answer.
 * - Before resolution, falls back to platform detection via GCInstant sync APIs
 *   (optimistic — assumes payments will be available on supported platforms).
 *
 * Intended to replace: {@link canAttemptPayments}, {@link isIapReady},
 * {@link arePaymentsAvailable}, `isLineIAPAvailable`.
 */
export function isPaymentAvailable(state: State): boolean {
  // Dev settings override — enables IAP in local development
  if (devSettings.get('enableIAP')) {
    trackDebugIsPaymentAvailable(state.iap, true);
    return true;
  }

  // Guest platform exclusion — only iOS app guests can use IAP
  if (isPlatformGuest(platform) && platform.osType !== 'IOS_APP') {
    trackDebugIsPaymentAvailable(state.iap, false);
    return false;
  }

  const { iap } = state;

  // After IAP init has completed: use the definitive result.
  // `iap.ready` reflects whether the platform IAP SDK initialized successfully,
  // so once resolved this is the single source of truth.
  // Also verify the catalog is populated — `iap.ready` can be true while the
  // product catalog is still empty, causing ShopBundlesError.
  if (iap.resolved) {
    const isCatalogLoaded = platform.catalog.length > 0;
    const result = iap.ready && isCatalogLoaded;
    trackDebugIsPaymentAvailable(iap, result);
    return result;
  }

  // Before IAP init resolves we make an optimistic guess using synchronous
  // platform APIs. This avoids hiding purchase UI while the async init is
  // still in flight:
  // - LIFF `isApiAvailable('iap')` — checks LINE Mini App IAP capability.
  // - `legacyNative.purchases.isAvailable()` — checks native app store support.
  const arePurchasesAvailable = Boolean(
    window.liff?.isApiAvailable?.('iap') ||
    platform.legacyNative?.purchases?.isAvailable(),
  );

  trackDebugIsPaymentAvailable(iap, arePurchasesAvailable);
  return arePurchasesAvailable;
}

/**
 * Fires a one-shot diagnostic event the first time {@link isPaymentAvailable}
 * is called. Captures a snapshot of every signal that feeds into the payment
 * availability decision so we can debug mismatches in Amplitude.
 *
 * **Temporary** — remove once the unified `isPaymentAvailable` rollout is
 * validated and the legacy payment checks are fully migrated (#10586).
 */
let debugIsPaymentAvailableTracked = false;
function trackDebugIsPaymentAvailable(iap: State['iap'], result: boolean) {
  if (debugIsPaymentAvailableTracked) return;
  debugIsPaymentAvailableTracked = true;

  const isLiffIapAvailable = Boolean(window.liff?.isApiAvailable?.('iap'));
  const isNativePurchasesAvailable = Boolean(
    platform.legacyNative?.purchases?.isAvailable(),
  );

  track('DebugIsPaymentAvailable', {
    iapResolved: iap.resolved,
    iapReady: iap.ready,
    isPaymentAvailableResult: result,
    paymentsAvailable: platform.paymentsAvailable,
    iapInitSupported: Boolean(platform.supportedAPIs?.iapInit),
    isCatalogLoaded: platform.catalog.length > 0,
    catalogSize: platform.catalog.length,
    isLiffIapAvailable,
    isNativePurchasesAvailable,
    arePurchasesAvailable: isLiffIapAvailable || isNativePurchasesAvailable,
    osType: platform.osType ?? 'unknown',
    isGuest: isPlatformGuest(platform),
  });
}

/**
 * Waits for IAP to resolve before opening purchase UI. IAP resolves only
 * after the catalog load finishes.
 *
 * If IAP has already resolved, immediately returns the current payment
 * availability status via {@link isPaymentAvailable}.
 *
 * If IAP has not yet resolved, shows a loading spinner and waits up to
 * {@link timeoutSeconds} for resolution. On timeout, reports to Sentry and
 * Amplitude, shows an error popup, and returns `false`.
 *
 * Use this before opening any UI that depends on catalog data (prices,
 * product listings) to prevent ShopBundlesError / missing prices.
 *
 * @param origin - Identifies the caller feature, used in debug tracking.
 * @param timeoutSeconds - Maximum wait time in seconds (default: 20).
 * @returns `true` if payment is available, `false` otherwise.
 *
 * @example
 * ```ts
 * async function openMyShop(origin: string) {
 *   const ready = await waitForIapResolve(origin);
 *   if (!ready) return;
 *   // Safe to open shop UI — catalog is loaded
 *   await openPopupPromise('popupMyShop', {});
 * }
 * ```
 */
export async function waitForIapResolve(
  origin: string,
  timeoutSeconds = 20,
): Promise<boolean> {
  const { iap } = StateObserver.getState();

  // If IAP is already resolved, return the current availability status.
  // With the `enableIAP` dev setting IAP init is skipped and never resolves.
  if (iap.resolved || devSettings.get('enableIAP')) {
    return isPaymentAvailable(StateObserver.getState());
  }

  // Wait for IAP to resolve with a loading spinner
  const iapResolved = cancelableStatePromise(({ iap }) => iap.resolved);
  const resolved = await withLoading(() =>
    Promise.race([
      iapResolved.promise.then(() => true),
      delay(duration({ seconds: timeoutSeconds })).then(() => false),
    ]),
  );

  if (!resolved) {
    iapResolved.cancel();

    track('DebugCatalogLoadTimeout', {
      timeout: timeoutSeconds,
      origin,
      osType: platform.osType ?? 'unknown',
    });

    captureGenericError(
      'CatalogTimeout',
      `IAP not resolved after ${timeoutSeconds}s (origin: ${origin})`,
    );

    await openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });

    return false;
  }

  // IAP resolved — check the actual payment availability (includes catalog check)
  return isPaymentAvailable(StateObserver.getState());
}

/**
 * We should remove this. It breaks LIFF user shop experience.
 * We keep this only for control testing.
 * @deprecated Use {@link isPaymentAvailable} instead. Uses stale A/B test flags. Breaks LIFF.
 */
export const canAttemptPayments = (state: ReplicantState) => {
  // Payment always available in mini app
  if (isLineIAPAvailable()) {
    return true;
  }

  // The pure open web test does not display payment options in LIFF
  const removePayments =
    (!features(state).nativeIos && platform.osType === 'IOS') ||
    (!features(state).nativeAndroid && platform.osType === 'ANDROID');

  return !removePayments;
};

export function getProductDataById(productId: ProductId) {
  const product = platform.catalog.find((x) => x.id === productId);
  if (!product) {
    throw new Error(`Product ${productId} does not exist!`);
  }

  const rewards = Iap.utils.getProductRewards(productId);
  const price = realMoneyPriceToReadableString(
    product.price,
    product.currencyCode,
  );

  return {
    ...product,
    id: productId,
    rewards,
    price,
  };
}

// Check if product is in the runtime platform catalog
export const isProductAvailable = (productId: ProductId) =>
  platform.catalog.some((x) => x.id === productId);

export function getProductDescription(product: ProductReward) {
  // Only show bonus gems for iOS native app
  if (platform.osType !== 'IOS_APP') {
    return '';
  }

  const { paidGems, freeGems } = product;
  if (!freeGems) {
    // No bonus gems for this product
    return '';
  }

  return i18n('currencyExchange.gemsShop.bonus', {
    text: `${parseAmount(paidGems ?? 0)} + ${parseAmount(freeGems)}`,
  });
}

/**
 * Start purchase flow
 *
 * If IAP is not ready: fallback to alternative purchase flow
 * if IAP is ready: start purchase
 */
export async function startPurchaseFlow(opts: {
  productId: ProductId;
  feature: string;
  subFeature: string;
  skipPurchaseTapped?: boolean;
  onComplete?: (success: boolean) => void;
  onError?: (e: any) => void;
  onBeforeNativeSwitch?: () => Promise<void>;
  onBeforeReward?: () => Promise<void>;
  context?: DeepLink[];
}) {
  // Fall back to alternative purchase flow if IAP is not ready
  if (!isIapReady() && !isWebIapAvailable()) {
    return fallbackPurchaseFlow(opts);
  }

  const hasNotAcceptedPurchaseToS =
    platform.storage.linePurchaseTOSLastAcceptedAt === undefined;

  // Show LINE purchase ToS nudge if:
  if (
    // User is in LINE mini app
    isInLineMiniApp() &&
    // User has not accepted ToS yet
    hasNotAcceptedPurchaseToS
  ) {
    await openPopupPromise('popupMiniAppPurchaseToSNudge', {});
  }

  try {
    // We support only web payments or LINE mini app payments
    if (isWebIapAvailable() || isLineIAPAvailable()) {
      // Open web purchase flow
      await buyProductNew(opts.productId, {
        feature: opts.feature,
        subFeature: opts.subFeature,
        onComplete: opts.onComplete,
        onBeforeReward: opts.onBeforeReward,
      });
    } else {
      // Native purchase flow
      await buyProduct(opts);
    }
  } catch (e: any) {
    // Show in console
    console.error(e);
    // Ignore this error.
    if (e.message === 'paymentCancelled') {
      opts.onError?.(e);
      return;
    }
    // Report issue
    captureGenericError(`Purchase of product ${opts.productId} failed.`, e);
    // Failed to purchase
    opts.onError?.(e);
  }
}

/**
 * Fallback purchase flow:
 * For Guest users, prompt to login.
 * For native users: crossplay trigger
 * For rest users: open appstore page
 */
async function fallbackPurchaseFlow(opts: {
  productId: ProductId;
  feature: string;
  subFeature: string;
  skipPurchaseTapped?: boolean;
  onComplete?: (success: boolean) => void;
  onError?: (e: any) => void;
  onBeforeNativeSwitch?: () => Promise<void>;
  context?: DeepLink[];
}) {
  // Prompt player to login if they are a guest.
  if (isPlatformGuest(platform)) {
    await promptLINELoginNew('purchase');
    return;
  }

  // Track the purchase attempt
  trackPurchaseTapped({
    feature: opts.feature,
    subFeature: opts.subFeature + '-fake',
    ...predictRevenueGrossAnalytics(opts.productId),
    purchaseProvider: 'line' as PurchaseProvider, // LIFF Fallback case
    purchaseProviderMethod: 'line_app' as PaymentMethod, // LIFF Fallback case
    purchaseCountBefore: StateObserver.replicant.getPurchaseHistory().length,
    purchaseProviderCreditCardBrand: undefined,
    purchaseID: 'unknown',
  });

  // Tracking for the crossplay trigger.
  const now = StateObserver.now();

  // Try to open the native app.
  if (!isPlatformNative() && guessInstalledNativeAppPlatform()) {
    // Wait for any before switch logic to complete.
    if (opts.onBeforeNativeSwitch) {
      await withLoading(() => opts.onBeforeNativeSwitch!(), {
        feature: Feature.IAP._,
        subFeature: Feature.IAP.BEFORE_NATIVE_SWITCH,
      });
    }

    // Try to open the app.
    const appOpen = await startNativeCrossplay({
      payload: {
        $deepLink: opts.context,
      },
      tracking: {
        origin: 'purchase',
        feature: opts.feature,
        subFeature: opts.subFeature,
        timestamp: now,
        productID: opts.productId,
      },
    });
    if (appOpen) return;
  }

  // Otherwise open the appstore page.
  await openNativeAppStorePage({
    payload: {
      $deepLink: opts.context,
    },
    tracking: {
      origin: 'purchase',
      feature: opts.feature,
      subFeature: opts.subFeature,
      timestamp: now,
    },
  });
}

/**
 * Main purchase function
 */
export async function buyProduct(opts: {
  productId: ProductId;
  feature: string;
  subFeature: string;
  skipPurchaseTapped?: boolean;
  onComplete?: (success: boolean) => void;
  onBeforeReward?: () => Promise<void>;
}): Promise<void> {
  // In development mode, offer option to cheat purchase, which will give rewards straight away
  if (process.env.IS_DEVELOPMENT && devSettings.get('enableIAP')) {
    const result = await openPopupPromise('popupConfirmation', {
      title: 'Cheat Purchase',
      message: `Do you want to cheat purchase\n${opts.productId}?`,
      buttonOkay: 'Cheat Purchase',
      buttonCancel: 'Normal Purchase',
    });
    shopBundlesEnablePurchaseSimulation(!!result);
  }

  const { productId } = opts;
  const product = getProductDataById(productId);

  if (!product) {
    throw Error(`Product ${productId} does not exist in catalog!`);
  }

  console.log(`Buying ${productId} for ${product.price}`);

  const tappedTime = StateObserver.now();
  StateObserver.dispatch(setPurchaseTime({ timeNow: serverSyncedLocalTime() }));

  const analytics = {
    feature: opts.feature,
    subFeature: opts.subFeature,
  };

  // Generic revenue analytics
  // It will be updated during the purchase flow
  const initialAnalytics: IAPAnalytics = {
    ...analytics,
    ...(predictRevenueGrossAnalytics(productId) as RevenueAnalytics),
    purchaseCountBefore: StateObserver.replicant.getPurchaseHistory().length,
    purchaseID: 'unknown',
  };

  if (!opts.skipPurchaseTapped) {
    // NB: with web payments the purchase provider is selected in purchaseAsync
    trackPurchaseTapped(initialAnalytics);
  }

  const beforeItems = Inventory.getters.getCurrencies(
    StateObserver.getState().user,
    tappedTime,
  );

  // Show loading if it's taking longer.
  void showLoadingAnimation();

  try {
    // Execute async purchase sequence
    const { response, revenue, purchaseID } = await purchaseAsync(
      productId,
      analytics,
    );

    // Merge actual revenue analytics with initial analytics
    const iapAnalytics: IAPAnalytics = {
      ...initialAnalytics,
      ...revenue,
      purchaseID,
    };

    // If purchase method require redirect
    if (isPurchaseRedirect(response)) {
      // Wait analytics to be sent before redirect.
      await analyticsModule.flush();

      // Purchase requires a redirect, so navigate to the redirect URL and
      // pick the flow back up when the user re-enters the game
      location.assign(response.redirectUrl);

      // Wait for the visibility to return.
      await waitForVisibilityChange();

      // Stop the purchase flow, this will only be trigered if the user presses back on the browser.
      return Promise.reject(new Error('paymentCancelled'));
    }

    // Get items analytics after purchase
    const afterItems = Inventory.getters.getCurrencies(user(), tappedTime);
    const currencies = { old: beforeItems, new: afterItems };

    // Run pre-reward callback.
    if (opts.onBeforeReward) {
      hideLoadingAnimation();
      await opts.onBeforeReward();
    }

    // Show success popup and track purchase revenue analytics
    void completePurchase(productId, iapAnalytics, currencies, tappedTime).then(
      () => opts.onComplete?.(true),
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    const userCancelled =
      err.code === 'USER_INPUT' ||
      err.code === 'paymentCancelled' ||
      err.message === 'paymentCancelled' ||
      err.message === 'The user cancelled the purchase';

    trackPurchaseFailure({
      ...initialAnalytics,
      revenueNet: getPossibleNetRevenueFromError(productId, err),
      elapsedSeconds: (StateObserver.now() - tappedTime) / 1000,
      userCancelled,
      errorMessage: err.message,
      errorCode: userCancelled ? 'USER_INPUT' : err.code,
      purchaseProvider: err.provider,
      purchaseProviderMethod: err.providerMethod,
    });

    // Ignore user canceling the purchase dialog.
    if (userCancelled) {
      // Hide loading before showing purchase canceled popup.
      hideLoadingAnimation();

      // Show purchase canceled popup with support button
      await purchaseCanceledSequence(initialAnalytics);

      // Show it again to avoid the loading spinner errors
      void showLoadingAnimation();

      return Promise.reject(new Error('paymentCancelled'));
    }

    // Hide loading before showing error popup.
    hideLoadingAnimation();

    // Show error popup
    await purchaseFailedSequence(initialAnalytics, 'failed');

    // Show loading again.
    void showLoadingAnimation();

    // Rethrow error
    return Promise.reject(err);
  } finally {
    hideLoadingAnimation();
  }
}

function getPurchaseProviderData(revenueOpts: IAPAnalytics) {
  let purchaseProvider: PurchaseProvider | undefined;
  let purchaseProviderMethod: PaymentMethod | undefined;

  switch (revenueOpts.purchaseProvider) {
    case 'apple':
    case 'google':
      purchaseProvider = revenueOpts.purchaseProvider;
      purchaseProviderMethod = `${purchaseProvider}_native`;
      break;
    default: // Open web
      purchaseProvider = revenueOpts.purchaseProvider;
      purchaseProviderMethod = revenueOpts.purchaseProviderMethod;
  }

  return {
    purchaseProvider,
    purchaseProviderMethod,
  };
}

export async function completePurchase(
  productId: ProductId,
  iapAnalytics: IAPAnalytics,
  currencies: CurrencyAnalytics<Currencies>,
  tappedTime: number,
) {
  const { paidGems = 0, freeGems = 0 } = Iap.utils.getProductRewards(
    productId,
    user(),
    StateObserver.now(),
  );

  trackRevenue({
    revenueType: 'in_app_purchase',
    ...iapAnalytics, // Use properties from platform purchase response
    paidGems,
    freeGems,
    ...getPurchaseProviderData(iapAnalytics),
  });

  const timePurchaseSuccess = StateObserver.now();
  trackPurchaseSuccess({
    elapsedSeconds: (timePurchaseSuccess - tappedTime) / 1000,
    beforeFreeGems: currencies.old.freeGems,
    beforePaidGems: currencies.old.paidGems,
    afterFreeGems: currencies.new.freeGems,
    afterPaidGems: currencies.new.paidGems,
    ...iapAnalytics,
    // Override platform revenue data with the actual purchase provider data
    ...getRevenueGrossAnalytics(productId, {
      currency: iapAnalytics.currencyCodeLocal,
      price: iapAnalytics.revenueGrossLocal,
      provider: iapAnalytics.purchaseProvider!,
      providerMethod: iapAnalytics.purchaseProviderMethod!,
      providerCreditCardBrand: iapAnalytics.purchaseProviderCreditCardBrand,
    } as PurchaseMethod & {
      currency: string;
      price: number;
    }),
  });

  trackPinchConversion({
    action: 'purchase',
    feature: iapAnalytics.feature,
    subFeature: iapAnalytics.subFeature,
    resource: 'gems',
  });

  // Show the purchase complete popups with rewards
  await purchaseCompleteSequence(productId, iapAnalytics);
}

/**
 * If replicant is offline replace real platform.purchaseAsync to cheat
 */
async function purchaseAsync(
  productId: ProductId,
  analytics: Pick<IAPAnalytics, 'feature' | 'subFeature'>,
): Promise<PurchaseCompleteResult> {
  // Purchase simulation
  if (
    shopBundlesIsPurchaseSimulationEnabled() &&
    process.env.IS_DEVELOPMENT &&
    devSettings.get('enableIAP')
  ) {
    return cheatPurchase(productId, analytics) as any;
  }

  // Generic revenue analytics
  const genericAnalytics: IAPAnalytics = {
    ...analytics,
    ...(predictRevenueGrossAnalytics(productId) as RevenueAnalytics),
    purchaseCountBefore: StateObserver.replicant.getPurchaseHistory().length,
    purchaseID: 'unknown',
  };

  if (!isWebIapAvailable()) {
    // Delegate purchase to GCInstant
    const { purchase, product } = await platform.purchaseAsync(productId, {
      analytics: {
        ...genericAnalytics,
        absoluteIapSequence: genericAnalytics.purchaseCountBefore + 1,
      },
    });

    // Return revenue analytics
    return {
      response: { product },
      revenue: {
        ...genericAnalytics,
        currencyCodeLocal: purchase.currency ?? product.currencyCode,
        dollarToLocalRate:
          purchase.dollarToLocalRate ?? genericAnalytics.dollarToLocalRate,
        revenueGrossLocal: purchase.revenueGrossLocal,
        revenueGross: purchase.revenueGrossUSD ?? genericAnalytics.revenueGross,
      },
      purchaseID: purchase.id,
    };
  }

  // Handle web purchase flow
  const { response, revenue, purchaseID } = await openWebPurchaseSequence(
    productId,
    analytics,
  );

  // Return revenue analytics
  return { response, revenue: { ...genericAnalytics, ...revenue }, purchaseID };
}

/**
 * Purchase simulation
 */
async function cheatPurchase(
  productId: ProductId,
  analytics: Pick<IAPAnalytics, 'feature' | 'subFeature'>,
) {
  const price = Iap.ruleset.prices[productId];
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const product: Product = {
    title: productId,
    id: productId,
    description: productId,
    imageURI: '',
    price: formatter.format(price / 100),
    currencyCode: 'usd',
  };

  await StateObserver.invoke.cheat_purchase({
    productId,
  });

  await waitForItPromise(1000);

  return {
    response: { product },
    revenue: {
      ...analytics,
      ...predictRevenueGrossAnalytics(productId),
      productID: productId,
    },
  };
}

/**
 * Always returns false — was gated on platformID === 'line' which no longer exists.
 * TODO: Remove this function and its callers.
 */
export function isWebIapAvailable() {
  return false;
}

/**
 * Generates a CompletedPurchase DTO for a web purchase.
 *
 * @param productId - The ID of the product being purchased.
 * @returns A CompletedPurchase object containing the purchase details and the product information.
 */
export function getWebPurchaseDTO(productId: ProductId): CompletedPurchase {
  return {
    product: platform.catalog.find((product) => product.id === productId)!,
  };
}

export const isPlatformPaymentsAvailable = (): boolean => {
  return platform.supportedAPIs.iapInit || isWebIapAvailable();
};

export function shouldSkipPopupOnLaunchDuringPayment() {
  // Without web IAP the payment intent will not be handled
  // Similar (same) check is present in Application.ts
  if (!isPlatformPaymentsAvailable()) {
    return false;
  }

  const { checkoutProductId } = StateObserver.getState().user;

  return !!checkoutProductId;
}

export async function completeStripePayment(): Promise<void> {
  await withLoading(() => StateObserver.invoke.asyncCompleteStripePayment(), {
    feature: Feature.IAP._,
    subFeature: Feature.IAP.COMPLETE_STRIPE_PAYMENT,
  });
}

/**
 * Return location URL
 */
export function getReturnUrl() {
  const url = new URL(window.location.href);

  const { searchParams } = url;

  if (searchParams.has('payment-intent')) {
    searchParams.delete('payment-intent');
    searchParams.delete('hl');
  }

  if (searchParams.has('payload')) {
    searchParams.delete('payload');
  }

  if (searchParams.has('request_id')) {
    searchParams.delete('request_id');
  }

  if (searchParams.has('flow')) {
    searchParams.delete('flow');
  }

  if (searchParams.has('amp_device_id')) {
    searchParams.delete('amp_device_id');
  }

  return url.toString();
}

/**
 * Calculates the possible net revenue from an error object related to a specific product.
 *
 * This function attempts to extract the provider and provider method from the error object.
 * If both are found, it retrieves the net revenue associated with the product and the extracted
 * provider details. If either the provider or provider method is not found, it returns 0.
 *
 * @param productId - The ID of the product for which the revenue is being calculated.
 * @param err - The error object that may contain provider and provider method information.
 * @returns The possible net revenue if provider and provider method are found, otherwise 0.
 */
function getPossibleNetRevenueFromError(
  productId: ProductId,
  err: any,
): number {
  const providerMethod =
    err?.providerMethod ??
    err?.methodAnalytics?.purchaseProviderMethod ??
    err?.result?.providerMethod ??
    err?.result?.methodAnalytics?.purchaseProviderMethod;

  const provider = providerMethod
    ? methodToProviderMapping()[providerMethod as WebPaymentMethod]
    : undefined;

  if (provider && providerMethod) {
    const { revenueNet } = getRevenueGrossAnalytics(productId, {
      ...getRevenueBasePrice(productId),
      provider,
      providerMethod,
    });
    return revenueNet;
  }

  // Predict based on the product ID and platform
  return predictRevenueGrossAnalytics(productId).revenueNet;
}
